import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { SearchableSelect } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "@phosphor-icons/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/lib/constants/languages";
import { getResultLanguageOptions } from "@/lib/utils/languageOptions";
import { isKnownLanguage } from "@/lib/utils/language";

interface LanguageSelectorProps {
  name: string;
}

export function LanguageSelector({ name }: LanguageSelectorProps) {
  const { t } = useTranslation(["lessonLab", "common", "settings"]);
  const { control, watch } = useFormContext<any>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center justify-between mt-4">
          <FormLabel className="flex flex-row items-center flex-grow gap-2">
            {t("language", {
              ns: "common",
            })}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger className="cursor-pointer" type="button">
                  <Info size={24} />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-sm text-sm font-normal">
                    {t("languageTooltip", {
                      ns: "common",
                    })}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </FormLabel>
          <FormControl>
            <div id="tour-language-container">
              {(() => {
                const baseOptions = getResultLanguageOptions();
                const currentValue = field.value;
                const hasInOptions = baseOptions.some(
                  (o) => o.value === currentValue
                );
                const needsAdHocOption =
                  currentValue &&
                  !hasInOptions &&
                  currentValue !== LANGUAGES.OTHER;
                const options = needsAdHocOption
                  ? [
                      {
                        value: currentValue,
                        label: isKnownLanguage(currentValue)
                          ? baseOptions.find((o) => o.value === currentValue)
                              ?.label || currentValue
                          : `${currentValue} (custom)`,
                      },
                      ...baseOptions,
                    ]
                  : baseOptions;
                return (
                  <SearchableSelect
                    value={currentValue || ""}
                    onValueChange={field.onChange}
                    options={options}
                    placeholder={t("language.placeholder", { ns: "common" })}
                    className="flex flex-row items-center justify-between min-w-fit w-max"
                  />
                );
              })()}
            </div>
          </FormControl>
          <>
            {(watch(name) === LANGUAGES.OTHER ||
              !Object.values(LANGUAGES).includes(watch(name) as LANGUAGES)) && (
              <FormItem className="mt-4">
                <FormControl>
                  <Input
                    placeholder={t("language.placeholder", {
                      ns: "common",
                    })}
                    {...field}
                    value={watch(name) === LANGUAGES.OTHER ? "" : watch(name)}
                  />
                </FormControl>
              </FormItem>
            )}
          </>
        </FormItem>
      )}
    />
  );
}
